import React, {useRef, useState, useEffect, useMemo} from 'react';
import {Container, Row, Col, InputGroup, Button, FormSelect} from 'react-bootstrap';
import ReactDatePicker from 'react-datepicker';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCalendar, faFileExcel, faChevronRight, faChevronDown} from '@fortawesome/free-solid-svg-icons';
import {
    dateToString, dateWithMaxTime, dateWithZeroTime, textToFloat, numberWithCommas,
    zeroToEmptyStringAsCurrencyWithCommas
} from '../../tools';
import {utils, writeFile} from 'xlsx';
import RRTable from '../../components/rr-table/RRTable';
import {collection, getDocs, query, where, orderBy} from 'firebase/firestore';
import {appFirestore} from '../../../index';
import {fuelInvoiceConverter} from '../fuel-invoices/fuelInvoiceConverter';
import {RRFuelInvoiceType} from '../../types/RRFuelInvoiceType';
import {logisticsFBConverter} from '../logistics/LogisticsFBConverter';
import {RRLogisticType} from '../logistics/RRLogisticType';
import PriceCalcModal from './PriceCalcModal';
import {getReportMonthOptions} from './reportMonthOptions';

const EventCostsReport: React.FC = () => {
    const today = dateWithZeroTime(new Date())!;
    const monthOptions = useMemo(() => getReportMonthOptions(today), [today]);
    const defaultMonthOption = monthOptions[1] ?? monthOptions[0];

    const [working, setWorking] = useState(false);
    const [periodFrom, setPeriodFrom] = useState<Date | undefined>();
    const [periodTo, setPeriodTo] = useState<Date | undefined>();
    const [selectedMonth, setSelectedMonth] = useState<string>(defaultMonthOption.value);
    const [dpPeriodFrom, setDpPeriodFrom] = useState<Date | undefined>(defaultMonthOption.periodFrom);
    const [dpPeriodTo, setDpPeriodTo] = useState<Date | undefined>(defaultMonthOption.periodTo);
    const [report, setReport] = useState<any[] | undefined>(undefined);
    const [errorLoading, setErrorLoading] = useState<any | undefined>(undefined);
    const [invoiceFetchErrors, setInvoiceFetchErrors] = useState<string[] | undefined>(undefined);
    type PriceSourceType = 'invoice' | 'logistic' | 'calculated' | 'none';
    const [modalData, setModalData] = useState<{show: boolean; price?: number | null; source?: PriceSourceType; truckLabel?: string | null; invoicesSlice?: RRFuelInvoiceType[] | null; note?: string}>({show: false, price: null, source: 'none', truckLabel: null, invoicesSlice: null, note: ''});

    const tblRef = useRef<HTMLTableElement>(null);
    const [expandedEvents, setExpandedEvents] = useState<Record<string, boolean>>({});
    const eventKeys = useMemo<string[]>(() => {
        if (!report || report.length === 0) return [];
        const keys = new Set<string>();
        report.forEach(r => {
            const key = String(r.eventNumber ?? '');
            keys.add(key);
        });
        return Array.from(keys);
    }, [report]);
    const areAllDetailsExpanded = eventKeys.length > 0 && eventKeys.every(key => Boolean(expandedEvents[key]));

    function getPriceCalculationNote(source: PriceSourceType): string {
        if (source === 'invoice') {
            return 'Използвана е фактурата от следващото зареждане, което затваря пробега за курса.';
        }
        if (source === 'logistic') {
            return 'Използвана е въведената в логистиката цена на км.';
        }
        return '';
    }

    function setRefPeriod() {
        setPeriodFrom(dateWithZeroTime(dpPeriodFrom));
        setPeriodTo(dateWithMaxTime(dpPeriodTo));
    }

    // Изборът на месец попълва началната и крайната дата с целия съответен период.
    function applyMonthSelection(optionValue: string) {
        const selectedOption = monthOptions.find(option => option.value === optionValue);
        if (!selectedOption) {
            return;
        }

        setSelectedMonth(selectedOption.value);
        setDpPeriodFrom(selectedOption.periodFrom);
        setDpPeriodTo(selectedOption.periodTo);
    }

    function exportExcel(): void {
        if (!tblRef.current) return;
        const workbook = utils.table_to_book(tblRef.current);
        const sheetName = workbook.SheetNames[0];
        if (sheetName) {
            const worksheet = workbook.Sheets[sheetName];
            const ref = worksheet['!ref'];
            if (ref) {
                const range = utils.decode_range(ref);
                const columnMax: number[] = [];
                for (let r = range.s.r; r <= range.e.r; r += 1) {
                    for (let c = range.s.c; c <= range.e.c; c += 1) {
                        const address = utils.encode_cell({r, c});
                        const cell = worksheet[address];
                        const value = cell ? (cell.v ?? '') : '';
                        const text = String(value);
                        const length = text.split(/\r?\n/).reduce((max, part) => Math.max(max, part.length), 0);
                        columnMax[c] = Math.max(columnMax[c] ?? 0, Math.max(length, 1));
                    }
                }
                worksheet['!cols'] = columnMax.map(len => ({wch: Math.min(Math.max(len + 2, 10), 60)}));
            }
        }
        writeFile(workbook, 'EventCostsReport.xlsx');
    }

    function toggleEvent(eventNumber: string | number) {
        const key = String(eventNumber);
        setExpandedEvents(prev => ({...prev, [key]: !prev[key]}));
    }

    function toggleAllDetails(): void {
        const expand = !areAllDetailsExpanded;
        setExpandedEvents(prev => {
            const next: Record<string, boolean> = {...prev};
            eventKeys.forEach(key => {
                next[key] = expand;
            });
            return next;
        });
    }

    // Placeholder compute: in future replace with real Firestore queries/aggregations
    useEffect(() => {
        setReport(undefined);
        setErrorLoading(undefined);
        if (periodFrom && periodTo) {
            const doCalc = async () => {
                try {
                    setWorking(true);

                    // Query logistics where reportStartDate is in period AND basic conditions
                    // Use the logisticsFBConverter to get typed RRLogisticType objects
                    const collRef = collection(appFirestore, 'logistics').withConverter(logisticsFBConverter);

                    const qStart = query(
                        collRef,
                        where('isOwn', '==', true),
                        where('workInStore', '==', false),
                        where('reportStartDate', '>=', periodFrom),
                        where('reportStartDate', '<=', periodTo),
                        // Note: don't use inequality filters on other fields due to Firestore limitations
                    );

                    const snapStart = await getDocs(qStart);

                    // gather all truckIds referenced by the logistics in this result set
                    const truckIdsSet = new Set<string>();
                    snapStart.docs.forEach((d: any) => {
                        try {
                            const lg = d.data() as RRLogisticType;
                            const tid = (lg as any)?.truck?.id ?? (lg as any)?.truckId ?? '';
                            if (tid) truckIdsSet.add(tid);
                        } catch (_e) {
                            // ignore
                        }
                    });
                    const truckIds = Array.from(truckIdsSet);

                    // For each truck fetch all invoices ordered by date.
                    // The report uses the next refueling that closes the travelled distance for a course.
                    const invColl = collection(appFirestore, 'fuelInvoices').withConverter(fuelInvoiceConverter);
                    const localInvoiceErrors: string[] = [];
                    const invoicesMap = new Map<string, RRFuelInvoiceType[]>();
                    const batchSize = 10;
                    const invoiceWindowFrom = periodFrom ? new Date(periodFrom) : null;
                    if (invoiceWindowFrom) {
                        invoiceWindowFrom.setDate(invoiceWindowFrom.getDate() - 30);
                    }
                    for (let i = 0; i < truckIds.length; i += batchSize) {
                        const batch = truckIds.slice(i, i + batchSize);
                        const promises = batch.map(tid => {
                            const qInvoices = invoiceWindowFrom && periodTo
                                ? query(
                                    invColl,
                                    where('truckId', '==', tid),
                                    where('invDate', '>=', invoiceWindowFrom),
                                    where('invDate', '<=', periodTo),
                                    orderBy('invDate', 'asc')
                                )
                                : query(invColl, where('truckId', '==', tid), orderBy('invDate', 'asc'));
                            return getDocs(qInvoices)
                                .then(async snap => {
                                    if (!snap.empty || !invoiceWindowFrom || !periodTo) {
                                        return {tid, snap};
                                    }
                                    const qFallback = query(invColl, where('truckId', '==', tid), orderBy('invDate', 'asc'));
                                    const fallbackSnap = await getDocs(qFallback);
                                    return {tid, snap: fallbackSnap};
                                })
                                .catch(err => ({tid, err}));
                        });
                        const results = await Promise.all(promises);
                        results.forEach((res: any) => {
                            try {
                                if (res && res.err) {
                                    // eslint-disable-next-line no-console
                                    console.warn('EventCostsReport: error fetching invoices for truck', res.tid, res.err);
                                    localInvoiceErrors.push(`Error fetching invoices for truck ${res.tid}: ${String(res.err?.message || res.err)}`);
                                    return;
                                }
                                if (res && res.snap && res.snap.docs && res.snap.docs.length > 0) {
                                    const arr: RRFuelInvoiceType[] = res.snap.docs.map((d: any) => d.data() as RRFuelInvoiceType);
                                    arr.sort((a,b) => (a.invDate?.getTime()||0) - (b.invDate?.getTime()||0));
                                    invoicesMap.set(res.tid, arr);
                                }
                            } catch (_e) {
                                // ignore per-truck failure
                            }
                        });
                    }

                    // if we collected any invoice fetch errors, expose them to the UI
                    if (localInvoiceErrors.length > 0) setInvoiceFetchErrors(localInvoiceErrors);

                    const all = snapStart.docs.map(d => {
                         // Use converter output as typed RRLogisticType
                         const logistic = d.data() as RRLogisticType;

                         // Normalize/parse startKm and endKm to numbers robustly
                         let startKm: number | null = (typeof logistic.startKm === 'number') ? logistic.startKm : (logistic.startKm ?? null);
                         let endKm: number | null = (typeof logistic.endKm === 'number') ? logistic.endKm : (logistic.endKm ?? null);

                         // If converter didn't yield numeric values, try raw snapshot fields
                         try {
                             // @ts-ignore
                             const rawStart = (startKm === null || startKm === 0) && typeof d.get === 'function' ? d.get('startKm') : undefined;
                             const rawEnd = (endKm === null || endKm === 0) && typeof d.get === 'function' ? d.get('endKm') : undefined;
                             if (rawStart !== undefined && rawStart !== null) {
                                 const p = textToFloat(rawStart);
                                 if (p !== null) startKm = p;
                                 else if (typeof rawStart === 'number') startKm = rawStart;
                             }
                             if (rawEnd !== undefined && rawEnd !== null) {
                                 const p = textToFloat(rawEnd);
                                 if (p !== null) endKm = p;
                                 else if (typeof rawEnd === 'number') endKm = rawEnd;
                             }
                         } catch (_e) {
                            // ignore
                         }

                        // Try to extract pricePerKm from known fields (converter or raw snapshot)
                        let pricePerKm: number | null = null;
                        // invoicesSliceVar will hold up to 3 invoices used for averaging (declared at map scope so it's available when creating the object)
                        let invoicesSliceVar: RRFuelInvoiceType[] | null = null;
                        try {
                            // check common logistic fields
                            const cand = (logistic as any).pricePerKm ?? (logistic as any).price_per_km ?? (logistic as any).priceKm ?? (logistic as any).price ?? null;
                            if (cand !== null && cand !== undefined) {
                                const p = textToFloat(cand);
                                if (p !== null) pricePerKm = p;
                            }
                            // fallback to raw snapshot get
                            if ((pricePerKm === null || pricePerKm === 0) && typeof d.get === 'function') {
                                // @ts-ignore
                                const rawP = d.get('pricePerKm') ?? d.get('price_per_km') ?? d.get('price') ?? d.get('price_km');
                                if (rawP !== undefined && rawP !== null) {
                                     const p2 = textToFloat(rawP);
                                     if (p2 !== null) pricePerKm = p2;
                                     else if (typeof rawP === 'number') pricePerKm = rawP;
                                }
                             }
                        } catch (_e) {
                            // ignore
                        }

                        // If no pricePerKm on logistic, try to get it from invoices for the truck
                        let priceSourceVar: string | null = null;
                        let chosenInvoiceVar: RRFuelInvoiceType | null = null;
                        try {
                            if (pricePerKm !== null && pricePerKm > 0) {
                                priceSourceVar = 'logistic';
                            }
                            if ((!pricePerKm || pricePerKm === 0) && logistic) {
                                const truckId = (logistic as any).truck?.id ?? (logistic as any).truckId ?? '';
                                if (truckId) {
                                    const invs = invoicesMap.get(truckId);
                                    if (invs && invs.length > 0) {
                                        const reportDate = logistic.reportStartDate ? new Date(logistic.reportStartDate) : (logistic.startDate ? new Date(logistic.startDate) : null);
                                        const normalizedReportDate = reportDate ? dateWithZeroTime(reportDate) : null;
                                        const closingInvoice = normalizedReportDate
                                            ? invs.find(inv => {
                                                const normalizedInvoiceDate = dateWithZeroTime(inv.invDate);
                                                return normalizedInvoiceDate ? normalizedInvoiceDate >= normalizedReportDate : false;
                                            }) ?? null
                                            : null;

                                        if (closingInvoice) {
                                            const computedAvg = (closingInvoice.avgPriceForKm !== undefined && closingInvoice.avgPriceForKm !== null)
                                                ? Number(closingInvoice.avgPriceForKm)
                                                : ((closingInvoice.totalKm && Number(closingInvoice.totalKm) > 0)
                                                    ? ((Number(closingInvoice.fuelAmount || 0) * Number(closingInvoice.price || 0)) / Number(closingInvoice.totalKm || 1))
                                                    : 0);
                                            if (!pricePerKm || pricePerKm === 0) {
                                                pricePerKm = Number(computedAvg || 0);
                                                priceSourceVar = 'invoice';
                                            }
                                            invoicesSliceVar = [closingInvoice];
                                            chosenInvoiceVar = closingInvoice;
                                        }
                                    }
                                }
                            }
                        } catch (_e) {
                            // ignore
                        }

                        const obj: any = {
                            ...logistic,
                            id: logistic.id || d.id,
                            startKm: startKm,
                            endKm: endKm,
                            pricePerKm: pricePerKm,
                            priceSource: priceSourceVar,
                            chosenInvoice: chosenInvoiceVar,
                            invoicesSlice: invoicesSliceVar,
                            _rawSnapshot: undefined
                        };

                         return obj;
                     });

                    // debug: log small sample showing parsed vs raw endKm and pricePerKm
                    try {
                        const sample = all.slice(0, 6).map(a => ({id: a.id, startKm: a.startKm, endKm: a.endKm, pricePerKm: a.pricePerKm, rawEndKm: a._raw?.endKm || a._raw?.end_km || null}));
                        // eslint-disable-next-line no-console
                        console.debug('EventCostsReport: parsed sample', sample);
                    } catch (_e) {}

                     // Debug: if raw doc contains endKm but parsed obj has no endKm, log one example to console
                     try {
                         const mismatch = all.find(a => (a.endKm === undefined || a.endKm === null || Number(a.endKm) === 0) && a._raw && (a._raw.endKm !== undefined || a._raw.end_km !== undefined || a._raw['end_km'] !== undefined));
                         if (mismatch) {
                             // eslint-disable-next-line no-console
                             console.warn('EventCostsReport: parsed endKm is empty but raw contains endKm', {id: mismatch.id, raw: mismatch._raw, parsedEndKm: mismatch.endKm});
                         }
                     } catch (_e) {
                         // ignore
                     }

                    // Client-side strict filtering
                    const filtered = all.filter((l: any) => {
                        if (!l) return false;
                        if (!l.reportStartDate || !l.reportEndDate) return false;
                        // require numeric eventNumber > 0
                        if (l.eventNumber === undefined || l.eventNumber === null) return false;
                        const ev = Number(l.eventNumber);
                        if (isNaN(ev) || ev <= 0) return false;
                        if (!l.isOwn) return false;
                        if (l.startKm === undefined || l.startKm === null) return false;
                        if (l.endKm === undefined || l.endKm === null) return false;
                        const s = Number(l.startKm);
                        const e = Number(l.endKm);
                        if (isNaN(s) || isNaN(e)) return false;
                        // both must be non-zero and endKm must be greater than startKm
                        if (s === 0 || e === 0) return false;
                        return e > s;
                    });

                    setReport(filtered.map((l: any, idx: number) => ({...l, __idx: idx + 1})));
                } catch (e) {
                    setErrorLoading(e as any);
                    setReport([]);
                } finally {
                    setWorking(false);
                }
            };
            void doCalc();
        }
    }, [periodFrom, periodTo]);

    // dates can be computed later when needed

    return (
        <Container fluid>
            <h1>Справки и отчети</h1>
            <h3>Разход по събития</h3>

            <Row className={'g-2 mt-3 flex-nowrap'}>
                <Col>
                    <Row className={'lh-2-5em flex-nowrap'}>
                        <Col xs={'auto'}>
                            <Row>
                                <Col xs={'auto'} className={'lh-2-5em'}>Месец:</Col>
                                <Col xs={'auto'}>
                                    <FormSelect
                                        className={'mb-2'}
                                        disabled={working}
                                        value={selectedMonth}
                                        onChange={event => applyMonthSelection(event.target.value)}
                                    >
                                        {
                                            monthOptions.map(option => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))
                                        }
                                    </FormSelect>
                                </Col>
                            </Row>
                        </Col>
                        <Col xs={'auto'}>
                            <Row>
                                <Col xs={'auto'} className={'lh-2-5ем'}>Начална дата:</Col>
                                <Col xs={'auto'}>
                                    <InputGroup className="mb-2 w-180px flex-nowrap">
                                        <ReactDatePicker
                                            disabled={working}
                                            id={'periodFromDate'}
                                            className={'form-control flex-nowrap'}
                                            selected={dpPeriodFrom}
                                            onChange={date => setDpPeriodFrom(date || defaultMonthOption.periodFrom)}
                                            value={dateToString(dpPeriodFrom)}
                                            maxDate={dpPeriodTo}
                                        />
                                        <label htmlFor={'periodFromDate'}>
                                            <InputGroup.Text className={'lh-2-5ем'}>
                                                <FontAwesomeIcon icon={faCalendar} size={'xl'}/>
                                            </InputGroup.Text>
                                        </label>
                                    </InputGroup>
                                </Col>
                            </Row>
                        </Col>
                        <Col xs={'auto'}>
                            <Row>
                                <Col xs={'auto'} className={'lh-2-5ем'}>Крайна дата:</Col>
                                <Col xs={'auto'}>
                                    <InputGroup className="mb-2 w-180px flex-nowrap">
                                        <ReactDatePicker
                                            disabled={working}
                                            id={'periodToDate'}
                                            className={'form-control flex-nowrap'}
                                            selected={dpPeriodTo}
                                            onChange={date => setDpPeriodTo(date || today)}
                                            value={dateToString(dpPeriodTo)}
                                            minDate={dpPeriodFrom}
                                        />
                                        <label htmlFor={'periodToDate'}>
                                            <InputGroup.Text className={'lh-2-5ем'}>
                                                <FontAwesomeIcon icon={faCalendar} size={'xl'}/>
                                            </InputGroup.Text>
                                        </label>
                                    </InputGroup>
                                </Col>
                            </Row>
                        </Col>

                        <Col>
                            {!working && <>
                                <Button onClick={setRefPeriod}>Изчисли</Button>&nbsp;
                                <Button onClick={() => exportExcel()} variant={'outline-success'}>
                                    <FontAwesomeIcon icon={faFileExcel}/>
                                </Button>&nbsp;
                            </>}
                        </Col>
                    </Row>
                </Col>
            </Row>

            {
                // Show report loading
                working &&
                <Row className={'g-2 mt-3'}>
                    <Col xs={'12'}>
                        <div className={'badge bg-info p-2'}>Изготвяне на справката, моля изчакайте...</div>
                    </Col>
                </Row>
            }

            {
                // Show report loading error
                errorLoading &&
                <Row className={'g-2 mt-3'}>
                    <Col xs={'12'}>
                        <div className={'badge bg-danger p-2'}>Възникна грешка при зареждане: {String(errorLoading.message || errorLoading)}</div>
                    </Col>
                </Row>
            }

            {
                // Show no data found
                !working && !errorLoading && report && report.length === 0 &&
                <Row className={'g-2 mt-3'}>
                    <Col xs={'12'}>
                        <div className={'badge bg-warning p-2'}>Няма намерени данни за посочения период...</div>
                    </Col>
                </Row>
            }

            <Row className={'g-2 mt-3'}>
                <Col xs={12}>
                    {/* Render table only when we have data */}
                    {report && report.length > 0 ? (
                        <>
                        <RRTable ref={tblRef}>
                            <thead>
                            <tr>
                                <th className={'text-center'}>№</th>
                                <th className={'text-center'} style={{width: '36px'}}>
                                    {report && report.length > 0 && (
                                        <Button
                                            variant={'link'}
                                            className={'p-0'}
                                            onClick={e => { e.stopPropagation(); e.preventDefault(); toggleAllDetails(); }}
                                            aria-label={areAllDetailsExpanded ? 'Затвори всички детайли' : 'Отвори всички детайли'}
                                            aria-pressed={areAllDetailsExpanded}
                                        >
                                            <FontAwesomeIcon icon={areAllDetailsExpanded ? faChevronDown : faChevronRight} />
                                        </Button>
                                    )}
                                </th>
                                <th className={'text-center'}>Дата</th>
                                <th className={'text-center'}>Събитие</th>
                                <th className={'text-center'}>Камион</th>
                                <th className={'text-center'}>Шофьор</th>
                                <th className={'text-end'}>Начален км</th>
                                <th className={'text-end'}>Краен км</th>
                                <th className={'text-end'}>Разлика (км)</th>
                                <th className={'text-end'}>Цена на км</th>
                                <th className={'text-end'}>Цена на км без ДДС</th>
                                <th className={'text-end'}>Всичко</th>
                                <th className={'text-end'}>Всичко без ДДС</th>
                            </tr>
                            </thead>
                            <tbody>
                            {(() => {
                                // group report rows by eventNumber
                                const groups = new Map<string, any[]>();
                                (report || []).forEach(r => {
                                    const key = String(r.eventNumber || '');
                                    if (!groups.has(key)) { groups.set(key, []); }
                                    groups.get(key)!.push(r);
                                });
                                // sort group keys by numeric eventNumber
                                const sortedKeys = Array.from(groups.keys()).sort((a, b) => {
                                    const na = Number(a);
                                    const nb = Number(b);
                                    if (!isNaN(na) && !isNaN(nb)) return na - nb;
                                    return a.localeCompare(b);
                                });

                                // render grouped rows: summary row per event, and child rows toggled by button
                                const rows: React.ReactNode[] = [];
                                // accumulate only the values from the last column of group rows
                                let grandTotal = 0;
                                sortedKeys.forEach((key, gi) => {
                                     let items = groups.get(key) || [];
                                    // sort items in group by truck name (fallback to truckId)
                                    items = items.slice().sort((a: any, b: any) => {
                                        const an = (a?.truck?.name || a?.truckId || '').toString();
                                        const bn = (b?.truck?.name || b?.truckId || '').toString();
                                        return an.localeCompare(bn);
                                    });
                                     // aggregate diff and total for the group
                                     let sumDiff = 0;
                                     let sumTotal = 0;
                                     items.forEach((r: any) => {
                                        const d = Number((Number(r.endKm || 0) - Number(r.startKm || 0)).toFixed(2));
                                        const p = (r.pricePerKm !== undefined && r.pricePerKm !== null) ? Number(r.pricePerKm) : 0;
                                        sumDiff += isFinite(d) ? d : 0;
                                        sumTotal += isFinite(d) && p ? d * p : 0;
                                    });
                                    const avgPrice = sumDiff > 0 ? (sumTotal / sumDiff) : 0;
                                    const eventLabel = key;
                                    const expanded = expandedEvents[key];

                                    // if group has only one item -> render group row using that item's values (no expand icon)
                                    if (items.length === 1) {
                                        const r = items[0];
                                        const diff = Number((Number(r.endKm || 0) - Number(r.startKm || 0)).toFixed(2));
                                        const price = (r.pricePerKm !== undefined && r.pricePerKm !== null) ? Number(r.pricePerKm) : 0;
                                        const total = Number((diff * price).toFixed(2));
                                        const priceExclVat = Number((price / 1.2).toFixed(2));
                                        const totalExclVat = Number((total / 1.2).toFixed(2));
                                        // add group total to grand total
                                        grandTotal += isFinite(Number(total)) ? Number(total) : 0;
                                        const truck = (r as any).truck || (r as any).truckId ? (r as any).truck : null;
                                        const truckLabel = truck ? `${truck.name || ''}${truck.plate ? ' (' + truck.plate + ')' : ''}`.trim() : ((r as any).truckId || '-');
                                        // compute safe source value
                                        const srcCandidate = (r as any).priceSource as PriceSourceType | undefined;
                                        const isValidSrc = srcCandidate === 'invoice' || srcCandidate === 'logistic' || srcCandidate === 'calculated' || srcCandidate === 'none';
                                        const sourceVal: PriceSourceType = isValidSrc ? (srcCandidate as PriceSourceType) : ((r as any).chosenInvoice ? 'invoice' : (price ? 'calculated' : 'none'));
                                        rows.push(
                                            <tr
                                                key={`event-single-${key}`}
                                                className={'table-secondary fw-bold'}
                                                onClick={() => {
                                                    if (!price) return;
                                                    setModalData({show: true, price: price, source: sourceVal, truckLabel: truckLabel, invoicesSlice: (r as any).invoicesSlice || null, note: getPriceCalculationNote(sourceVal)});
                                                }}
                                                onKeyDown={(e:any) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (!price) return; setModalData({show: true, price: price, source: sourceVal, truckLabel: truckLabel, invoicesSlice: (r as any).invoicesSlice || null, note: getPriceCalculationNote(sourceVal)}); } }}
                                                tabIndex={price ? 0 : undefined}
                                                role={price ? 'button' : undefined}
                                                style={{cursor: price ? 'pointer' : 'default'}}
                                            >
                                                <td className={'text-center'}>{gi + 1}</td>
                                                <td className={'text-center'}></td>
                                                <td className={'text-center'}>{dateToString(new Date(r.reportStartDate))}</td>
                                                <td className={'text-start'}>{r.eventNumber}</td>
                                                <td className={'text-start'}>{truckLabel}</td>
                                                <td className={'text-start'}>{r.driverName}</td>
                                                <td className={'text-end'}>{numberWithCommas(Number(r.startKm || 0))}</td>
                                                <td className={'text-end'}>{numberWithCommas(Number(r.endKm || 0))}</td>
                                                <td className={'text-end'}>{numberWithCommas(diff)}</td>
                                                <td className={'text-end'}>{price ? numberWithCommas(Number(price.toFixed(2))) : '-'}</td>
                                                <td className={'text-end'}>{price ? numberWithCommas(priceExclVat) : '-'}</td>
                                                <td className={'text-end'}>{price ? zeroToEmptyStringAsCurrencyWithCommas(total) : '-'}</td>
                                                <td className={'text-end'}>{price ? zeroToEmptyStringAsCurrencyWithCommas(totalExclVat) : '-'}</td>
                                            </tr>
                                        );
                                    } else {
                                        // summary row for group with multiple items
                                        rows.push(
                                            <tr
                                                key={`event-sum-${key}`}
                                                className={'table-secondary fw-bold'}
                                                onClick={() => toggleEvent(key)}
                                                onKeyDown={(e:any) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleEvent(key); } }}
                                                tabIndex={0}
                                                style={{cursor: 'pointer'}}
                                                role="button"
                                                aria-expanded={expanded}
                                            >
                                                <td className={'text-center'}>{gi + 1}</td>
                                                <td className={'text-center'}>
                                                    <Button variant={'link'} className={'p-0'} onClick={(e:any) => { e.stopPropagation(); toggleEvent(key); }}>
                                                        <FontAwesomeIcon icon={expanded ? faChevronDown : faChevronRight} />
                                                    </Button>
                                                </td>
                                                <td className={'text-center'}>-</td>
                                                <td className={'text-start'}>Събитие {eventLabel} (бр.: {items.length})</td>
                                                <td className={'text-center'}></td>
                                                <td className={'text-start'}>-</td>
                                                <td className={'text-start'}>-</td>
                                                <td className={'text-end'}>-</td>
                                                <td className={'text-end'}>{numberWithCommas(Number(sumDiff.toFixed(2)))}</td>
                                                <td className={'text-end'}>{avgPrice ? zeroToEmptyStringAsCurrencyWithCommas(Number(avgPrice.toFixed(2))) : '-'}</td>
                                                <td className={'text-end'}>{avgPrice ? zeroToEmptyStringAsCurrencyWithCommas(Number((avgPrice / 1.2).toFixed(2))) : '-'}</td>
                                                <td className={'text-end'}>{avgPrice ? zeroToEmptyStringAsCurrencyWithCommas(Number(sumTotal.toFixed(2))) : '-'}</td>
                                                <td className={'text-end'}>{avgPrice ? zeroToEmptyStringAsCurrencyWithCommas(Number((sumTotal / 1.2).toFixed(2))) : '-'}</td>
                                            </tr>
                                        );

                                        if (expanded) {
                                            // if expanded, include each detail row's total in grandTotal instead of the group summary
                                            items.forEach((r: any, idx: number) => {
                                                const diff = Number((Number(r.endKm || 0) - Number(r.startKm || 0)).toFixed(2));
                                                const price = (r.pricePerKm !== undefined && r.pricePerKm !== null) ? Number(r.pricePerKm) : 0;
                                                const total = Number((diff * price).toFixed(2));
                                                const priceExclVat = Number((price / 1.2).toFixed(2));
                                                const totalExclVat = Number((total / 1.2).toFixed(2));
                                                const truck = (r as any).truck || (r as any).truckId ? (r as any).truck : null;
                                                const truckLabel = truck ? `${truck.name || ''}${truck.plate ? ' (' + truck.plate + ')' : ''}`.trim() : ((r as any).truckId || '-');
                                                // add this detail's total to grandTotal
                                                grandTotal += isFinite(Number(total)) ? Number(total) : 0;
                                                rows.push(
                                                    <tr key={r.id || `ev-${key}-${idx}`}>
                                                        <td className={'text-center'}>{r.__idx}</td>
                                                        <td className={'text-center'}></td>
                                                        <td className={'text-center'}>{dateToString(new Date(r.reportStartDate))}</td>
                                                        <td>{r.eventNumber}</td>
                                                        <td>{truckLabel}</td>
                                                        <td>{r.driverName}</td>
                                                        <td className={'text-end'}>{numberWithCommas(Number(r.startKm || 0))}</td>
                                                        <td className={'text-end'}>{numberWithCommas(Number(r.endKm || 0))}</td>
                                                        <td className={'text-end'}>{numberWithCommas(diff)}</td>
                                                        <td className={'text-end'}>
                                                            {price ? (
                                                                <Button variant={'link'} className={'p-0'} onClick={() => {
                                                                    const srcCandidate = r.priceSource as PriceSourceType | undefined;
                                                                    const isValid = srcCandidate === 'invoice' || srcCandidate === 'logistic' || srcCandidate === 'calculated' || srcCandidate === 'none';
                                                                    const sourceVal: PriceSourceType = isValid ? (srcCandidate as PriceSourceType) : (r.chosenInvoice ? 'invoice' : (price ? 'calculated' : 'none'));
                                                                    setModalData({show: true, price: price, source: sourceVal, truckLabel: truckLabel, invoicesSlice: r.invoicesSlice || null, note: getPriceCalculationNote(sourceVal)});
                                                                }}>{numberWithCommas(Number(price.toFixed(2)))}</Button>
                                                            ) : '-'}
                                                        </td>
                                                        <td className={'text-end'}>{price ? numberWithCommas(priceExclVat) : '-'}</td>
                                                        <td className={'text-end'}>{(price ? zeroToEmptyStringAsCurrencyWithCommas(total) : '-')}</td>
                                                        <td className={'text-end'}>{(price ? zeroToEmptyStringAsCurrencyWithCommas(totalExclVat) : '-')}</td>
                                                    </tr>
                                                );
                                            });
                                        } else {
                                            // collapsed: include group summary total in grandTotal
                                            grandTotal += isFinite(Number(sumTotal)) ? Number(sumTotal) : 0;
                                        }
                                    }
                                });
                                // add grand total row: Общо
                                rows.push(
                                    <tr key="grand-total" className={'table-primary fw-bold'}>
                                        <td className={'text-center'} colSpan={10}>Общо</td>
                                        <td className={'text-end'}>-</td>
                                        <td className={'text-end'}>{zeroToEmptyStringAsCurrencyWithCommas(Number((grandTotal || 0).toFixed(2)))}</td>
                                        <td className={'text-end'}>{zeroToEmptyStringAsCurrencyWithCommas(Number(((grandTotal || 0) / 1.2).toFixed(2)))}</td>
                                    </tr>
                                );
                                return rows;
                            })()}
                            </tbody>
                        </RRTable>
                        {/* Show invoice fetch errors under the table so users can see failed invoice queries */}
                        {invoiceFetchErrors && invoiceFetchErrors.length > 0 && (
                            <div className={'mt-3'}>
                                <div className={'alert alert-danger p-2'}>
                                    <strong>Възникнаха грешки при зареждане на допълнителни фактури:</strong>
                                    <ul className={'mb-0 mt-2'}>
                                        {invoiceFetchErrors.map((err, i) => (
                                            <li key={i}><small>{err}</small></li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        )}
                        </>
                     ) : (
                         <div><strong>Моля, изберете период и натиснете "Изчисли"...</strong></div>
                     )}
                 </Col>
             </Row>

            <PriceCalcModal
                show={modalData.show}
                onHide={() => setModalData({...modalData, show: false})}
                price={modalData.price ?? null}
                truckLabel={modalData.truckLabel ?? null}
                invoicesSlice={modalData.invoicesSlice ?? null}
                note={modalData.note}
            />
        </Container>
     );
 }

 export default EventCostsReport;
